import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { defaultOnError } from 'util/conditional/onError';
import UserNotification from 'util/UserNotification';
import type SingletonParameter from 'views/logic/parameters/Parameter';
import type { SearchParams, Attribute } from 'stores/PaginationTypes';
import type { EntityBase } from 'components/common/EntityDataTable/types';
import { deserializeEventProcedures } from 'security-app/components/SecurityEvents/EventProcedures/plugin/EventProcedureLibraryForm/List/constants';
import { deserializeEventProcedureSteps } from 'security-app/components/SecurityEvents/EventProcedures/plugin/EventProcedureStepLibraryForm/List/constants';

import {
  fetchEventProcedures,
  fetchEventProcedure,
  fetchEventProcedureSteps,
  createEventProcedure,
  updateEventProcedure,
  createEventProcedureStep,
  deleteEventProcedure,
  deleteEventProcedureStep,
  updateEventProcedureStep,
  fetchDecoratedEventProcedure,
  fetchParametersForView,
  fetchLinkedProcedures,
} from './api/eventProceduresAPI';
import type {
  EventProceduresIndexAPIType,
  EventProceduresAPIType,
  EventProcedureStepAPIType,
  EventProcedureStepsIndexAPIType,
} from './api/eventProceduresAPI.types';

export type PaginatedProps = {
  page: number;
  perPage: number;
  query?: string;
  orderBy?: string;
  direction?: 'asc' | 'desc';
};

export const eventProceduresEntitiesKeyFn = (searchParams?: SearchParams) => ['event-procedures', 'list', searchParams];

export function useFetchEventProcedures(deserializer: any = deserializeEventProcedures) {
  const searchEventProcedures = async (
    searchParams: SearchParams,
  ): Promise<{
    attributes: Array<Attribute>;
    list: Array<EntityBase & EventProceduresAPIType<Array<EventProcedureStepAPIType>>>;
    pagination: { total: number; page: number; per_page: number; count: number };
  }> => {
    const {
      page,
      pageSize,
      query,
      sort: { attributeId, direction },
    } = searchParams;

    return fetchEventProcedures(page, pageSize, query, attributeId, direction).then(deserializer);
  };

  return { eventProceduresEntitiesKeyFn, searchEventProcedures };
}

export function useGetEventProcedures({
  page,
  perPage,
  query = undefined,
  orderBy = undefined,
  direction = undefined,
}: PaginatedProps) {
  const { data, isLoading } = useQuery<EventProceduresIndexAPIType, Error>(
    ['get-event-procedures', page, perPage, query, orderBy, direction],
    () =>
      defaultOnError(fetchEventProcedures(page, perPage, query, orderBy, direction), 'Error fetching event procedures'),
    {
      retry: 2,
      keepPreviousData: true,
    },
  );

  return {
    loadingEventProcedures: isLoading,
    eventProcedures: isLoading ? [] : data?.event_procedures,
    pagination: {
      page: data?.page || page,
      perPage: data?.per_page || perPage,
      total: data?.total || 0,
      grandTotal: data?.grand_total || 0,
      count: data?.count || 0,
    },
  };
}

export function useGetEventProcedure(eventProcedureId: string) {
  const { data, isFetching } = useQuery<EventProceduresAPIType<EventProcedureStepAPIType[]>, Error>(
    ['get-event-procedure', eventProcedureId],
    () => defaultOnError(fetchEventProcedure(eventProcedureId), 'Error fetching event procedure'),
    {
      enabled: !!eventProcedureId,
      retry: 2,
    },
  );

  return {
    loadingEventProcedure: isFetching,
    eventProcedure: isFetching ? {} : data,
  };
}

export function useGetDecoratedEventProcedure(eventProcedureId: string, eventId: string) {
  const { data, isFetching } = useQuery<EventProceduresAPIType<EventProcedureStepAPIType[]>, Error>(
    ['get-decorated-event-procedure', eventProcedureId, eventId],
    () => defaultOnError(fetchDecoratedEventProcedure(eventProcedureId, eventId), 'Error fetching event procedure'),
    {
      enabled: !!eventProcedureId,
      retry: 2,
    },
  );

  return {
    loadingEventProcedure: isFetching,
    eventProcedure: isFetching ? {} : data,
  };
}

export function useCreateEventProcedure() {
  const queryClient = useQueryClient();

  const { mutateAsync, data, isLoading } = useMutation(createEventProcedure, {
    onSuccess: () => {
      UserNotification.success('Event Procedure created successfully');
      queryClient.invalidateQueries(['event-procedures', 'list']);
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return {
    createEventProcedure: mutateAsync,
    eventProcedure: isLoading ? {} : data,
    creatingEventProcedure: isLoading,
  };
}

export function useUpdateEventProcedure() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(updateEventProcedure, {
    onSuccess: () => {
      UserNotification.success('Event Procedure updated successfully');
      queryClient.invalidateQueries(['event-procedures', 'list']);
      queryClient.invalidateQueries(['get-event-procedure']);
      queryClient.invalidateQueries(['get-decorated-event-procedure']);
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return {
    updateEventProcedure: mutateAsync,
    updatingEventProcedure: isLoading,
  };
}

export function useDeleteEventProcedure() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(deleteEventProcedure, {
    onSuccess: () => {
      UserNotification.success('Event Procedure deleted successfully');
      queryClient.invalidateQueries(['event-procedures', 'list']);
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return {
    deleteEventProcedure: mutateAsync,
    deletingEventProcedure: isLoading,
  };
}

export const eventProcedureStepsEntitiesKeyFn = (searchParams?: SearchParams) => [
  'event-procedure-steps',
  'list',
  searchParams,
];

export function useFetchEventProcedureSteps(deserializer: any = deserializeEventProcedureSteps) {
  const searchEventProcedureSteps = async (
    searchParams: SearchParams,
  ): Promise<{
    attributes: Array<Attribute>;
    list: Array<EntityBase & EventProcedureStepAPIType>;
    pagination: { total: number; page: number; per_page: number; count: number };
  }> => {
    const {
      page,
      pageSize,
      query,
      sort: { attributeId, direction },
    } = searchParams;

    return fetchEventProcedureSteps(page, pageSize, query, attributeId, direction).then(deserializer);
  };

  return { eventProcedureStepsEntitiesKeyFn, searchEventProcedureSteps };
}

export function useGetEventProcedureSteps({
  page,
  perPage,
  query = undefined,
  orderBy = undefined,
  direction = undefined,
}: PaginatedProps) {
  const { data, isLoading } = useQuery<EventProcedureStepsIndexAPIType, Error>(
    ['get-event-procedure-steps', page, perPage, query, orderBy, direction],
    () =>
      defaultOnError(
        fetchEventProcedureSteps(page, perPage, query, orderBy, direction),
        'Error fetching event procedure steps',
      ),
    {
      retry: 2,
      keepPreviousData: true,
    },
  );

  return {
    loadingEventProcedureSteps: isLoading,
    eventProcedureSteps: isLoading ? [] : data?.event_procedure_steps,
    pagination: {
      page: data?.page || page,
      perPage: data?.per_page || perPage,
      total: data?.total || 0,
      grandTotal: data?.grand_total || 0,
      count: data?.count || 0,
    },
  };
}

export function useCreateEventProcedureStep() {
  const queryClient = useQueryClient();

  const { mutateAsync, data, isLoading } = useMutation(createEventProcedureStep, {
    onSuccess: () => {
      UserNotification.success('Event Procedure Step created');
      queryClient.invalidateQueries(['event-procedure-steps', 'list']);
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return {
    createEventProcedureStep: mutateAsync,
    eventProcedureStep: isLoading ? {} : data,
    creatingEventProcedureStep: isLoading,
  };
}

export function useUpdateEventProcedureStep() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(updateEventProcedureStep, {
    onSuccess: () => {
      UserNotification.success('Event Procedure Step updated successfully');
      queryClient.invalidateQueries(['event-procedure-steps', 'list']);
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return {
    updateEventProcedureStep: mutateAsync,
    updatingEventProcedureStep: isLoading,
  };
}

export function useGetLinkedProceduresForStep(stepId: string) {
  const { data, isFetching } = useQuery<string[], Error>(
    ['get-procedures-using-step', stepId],
    () => defaultOnError(fetchLinkedProcedures(stepId), 'Error fetching linked event procedures'),
    {
      enabled: !!stepId,
      retry: 2,
    },
  );

  return {
    loadingLinkedEventProcedures: isFetching,
    linkedEventProcedures: isFetching ? [] : data,
  };
}

export function useDeleteEventProcedureStep() {
  const queryClient = useQueryClient();

  const { mutateAsync, isLoading } = useMutation(deleteEventProcedureStep, {
    onSuccess: () => {
      UserNotification.success('Event Procedure Step deleted successfully');
      setTimeout(() => queryClient.invalidateQueries(['event-procedure-steps', 'list']), 100);
    },
    onError: (error: Error) => UserNotification.error(error.message),
  });

  return {
    deleteEventProcedureStep: mutateAsync,
    deletingEventProcedureStep: isLoading,
  };
}

export function useGetParametersForView({ viewId, type }: { viewId: string; type: string }) {
  const { data, isFetching } = useQuery<SingletonParameter[], Error>(
    ['get-parameters-for-view', viewId, type],
    () => defaultOnError(fetchParametersForView(viewId, type), 'Error fetching parameters'),
    {
      enabled: !!viewId,
      retry: 2,
    },
  );

  return {
    fetchingParameters: isFetching,
    parameters: isFetching ? [] : data,
  };
}
